import cs from "classnames";
import React from "react";

import { useTrackFocusState } from "src/common/hooks/useTrackFocusState";

import "./Switch.css";

const noop = (): void => {};

interface SwitchProps {
	disabled?: boolean;
	checked?: boolean;
	onChangeChecked?(checked: boolean): unknown;
}

export const Switch: React.FunctionComponent<
	React.PropsWithChildren<SwitchProps>
> = ({ disabled, checked, onChangeChecked = noop, children }) => {
	const [isFocused, onFocus, onBlur] = useTrackFocusState();

	const onKeyDown = React.useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === " ") {
				event.preventDefault();
				onChangeChecked(!checked);
				onFocus();
			}
		},
		[onChangeChecked, onFocus, checked]
	);

	const onChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			onChangeChecked(event.target.checked);
		},
		[onChangeChecked]
	);

	return (
		<label
			className={cs("switch", {
				"switch--disabled": disabled,
				"switch--checked": checked,
				"switch--focused": isFocused
			})}
		>
			<div
				role="checkbox"
				aria-checked={checked}
				className="switch__input"
				tabIndex={disabled ? -1 : 0}
				onFocus={onFocus}
				onBlur={onBlur}
				onClick={onBlur}
				onKeyDown={onKeyDown}
			>
				<div className="switch__track" />
				<div className="switch__thumb">
					<div className="switch__ripple" />
					<div className="switch__circle" />
				</div>
				<input type="checkbox" checked={checked} onChange={onChange} />
			</div>
			{children && <div className="switch__label">{children}</div>}
		</label>
	);
};
